import type { ApiTodayPlan } from '@/features/kitchen/api/getTodayProduction'

export type PlanCatalogItem = {
  sku: string
  plan: number
  categoryName: string
  categorySortOrder: number
}

export type PlanLineRow = {
  sku: string
  plan: number
  stock: number
  need: number
  actualBySession: number[]
  totalActual: number
  categoryName: string
  categorySortOrder: number
}

export type PlanCategoryGroup<T> = {
  categoryName: string
  categorySortOrder: number
  items: T[]
}

type SessionRef = { ref: string; status: string }

type ProductionLineLike = { productName: string; qty: number }

export function productionNeed(plan: number, stock: number, dispatched: number): number {
  return plan - stock - dispatched
}

/** Katalog produk + kategori dari plan HO hari ini (dipakai produksi & distribusi). */
export function buildPlanCatalogFromTodayPlan(plan: ApiTodayPlan): PlanCatalogItem[] {
  const categoryByProduct = new Map<string, { name: string; sort: number }>()
  for (const item of plan.plans ?? []) {
    for (const line of item.lines) {
      const key = line.product_name.trim().toLowerCase()
      const cat = line.category_name?.trim()
      if (cat) {
        categoryByProduct.set(key, { name: cat, sort: line.category_sort_order ?? 9999 })
      }
    }
  }
  return (plan.merged_lines ?? []).map((line) => {
    const key = line.product_name.trim().toLowerCase()
    const fromPlan = categoryByProduct.get(key)
    const categoryName = line.category_name?.trim() || fromPlan?.name || 'Lainnya'
    return {
      sku: line.product_name,
      plan: line.target_qty,
      categoryName,
      categorySortOrder: line.category_sort_order ?? fromPlan?.sort ?? 9999,
    }
  })
}

export function orderedRecordingSessions(sessions: SessionRef[]): SessionRef[] {
  return sessions
    .filter((s) => s.status !== 'draft')
    .sort((a, b) => a.ref.localeCompare(b.ref))
}

export function computePlanLineRows(
  sessions: SessionRef[],
  sessionLinesByRef: Record<string, ProductionLineLike[]>,
  planCatalog: PlanCatalogItem[],
  stockByProduct: Record<string, number>,
  dispatchedByProduct: Record<string, number>,
  qtyForProduct: (map: Record<string, number>, name: string) => number,
  stockQtyForProduct: (map: Record<string, number>, name: string) => number,
): PlanLineRow[] {
  const recordingSessions = orderedRecordingSessions(sessions)

  return planCatalog.map((item) => {
    const stock = stockQtyForProduct(stockByProduct, item.sku)
    const dispatched = qtyForProduct(dispatchedByProduct, item.sku)
    const actualBySession = recordingSessions.map((session) => {
      let sum = 0
      for (const line of sessionLinesByRef[session.ref] ?? []) {
        if (line.qty <= 0 || !line.productName.trim()) continue
        if (line.productName.trim().toLowerCase() !== item.sku.trim().toLowerCase()) continue
        sum += line.qty
      }
      return sum
    })
    const totalActual = actualBySession.reduce((acc, qty) => acc + qty, 0)
    return {
      sku: item.sku,
      plan: item.plan,
      stock,
      need: productionNeed(item.plan, stock, dispatched),
      actualBySession,
      totalActual,
      categoryName: item.categoryName,
      categorySortOrder: item.categorySortOrder,
    }
  })
}

export function groupByCategory<T extends { categoryName: string; categorySortOrder: number }>(
  rows: T[],
): PlanCategoryGroup<T>[] {
  const map = new Map<string, PlanCategoryGroup<T>>()
  for (const row of rows) {
    const key = row.categoryName
    let group = map.get(key)
    if (!group) {
      group = {
        categoryName: row.categoryName,
        categorySortOrder: row.categorySortOrder,
        items: [],
      }
      map.set(key, group)
    }
    group.items.push(row)
  }
  return [...map.values()].sort((a, b) => {
    if (a.categorySortOrder !== b.categorySortOrder) return a.categorySortOrder - b.categorySortOrder
    return a.categoryName.localeCompare(b.categoryName)
  })
}

export function formatNeedCell(need: number): string {
  if (need > 0) return String(need)
  if (need < 0) return String(need)
  return '0'
}

export function groupSessionLinesByCategory<T extends { productName: string; source?: string }>(
  lines: T[],
  catalog: PlanCatalogItem[],
): PlanCategoryGroup<T>[] {
  const meta = new Map(
    catalog.map((item) => [
      item.sku.trim().toLowerCase(),
      { categoryName: item.categoryName, categorySortOrder: item.categorySortOrder },
    ]),
  )
  const enriched: Array<T & { categoryName: string; categorySortOrder: number }> = lines
    .filter((line) => line.source !== 'off_plan')
    .map((line) => {
      const cat = meta.get(line.productName.trim().toLowerCase())
      return {
        ...line,
        categoryName: cat?.categoryName ?? 'Lainnya',
        categorySortOrder: cat?.categorySortOrder ?? 9999,
      }
    })
  const groups = groupByCategory(enriched)
  return groups.map((group) => ({
    categoryName: group.categoryName,
    categorySortOrder: group.categorySortOrder,
    items: group.items.map(({ categoryName: _c, categorySortOrder: _s, ...rest }) => rest as unknown as T),
  }))
}

/** Pengelompokan baris distribusi / modul lain tanpa filter source. */
export function groupProductLinesByCategory<T extends { productName: string }>(
  lines: T[],
  catalog: PlanCatalogItem[],
): PlanCategoryGroup<T>[] {
  return groupSessionLinesByCategory(
    lines.map((line) => ({ ...line, source: 'plan' as const })),
    catalog,
  )
}

export function needCellClass(need: number): string {
  if (need > 0) return 'text-status-warning font-semibold'
  if (need < 0) return 'text-status-success'
  return 'text-on-surface'
}
